import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { IconDownload, IconRotate, IconRotateClockwise } from '@tabler/icons-react';
import { useDispatch, useSelector } from 'react-redux';
import { selectDisplayedGradient, selectGradientData } from '../../store/system/system.selector.js';
import { selectTeamIndex } from '../../store/user/user.selector.js';
import { setDisplayedGradient } from '../../store/system/system.action.js';
import GradientBG from '../../components/gradient-bg/GradientBG.js';
import PageNav from '../../components/system/page-nav/PageNav.js';
import { fileName, output, outputSize, R } from '../../utils/card-cropper/crop.js';
import useCardCropper from './useCardCropper.js';
import ScanStage from './ScanStage.js';
import {
  Bench,
  Btn,
  Check,
  Dims,
  Drop,
  DropOr,
  DropTitle,
  EditActions,
  EditDock,
  EditRow,
  Empty,
  Field,
  FieldLabel,
  FormatSelect,
  GradientWrap,
  HeaderWrap,
  Hint,
  Lead,
  Main,
  NavBtn,
  Page,
  Pane,
  PaneActions,
  PaneHead,
  Photo,
  PhotoFrame,
  PhotoGrid,
  PhotoName,
  PhotoRow,
  RangeRow,
  ScanBlock,
  ScanMeta,
  Scans,
  Status,
  Tag,
  TextInput,
  Tilt,
  Toolbar,
  ToolRow,
  Value,
  ViewButton,
  Viewer,
  ViewerBody,
  ViewerBox,
  ViewerHead,
  ViewerStage,
  ViewerTitle,
} from './CardCropperPage.styles.js';

function boxTag(box) {
  if (box.edited) return { label: 'edited', warn: false };
  if (box.manual) return { label: 'manual', warn: false };
  if (!box.cardLike) return { label: 'odd shape', warn: true };
  return null;
}

function CropCanvas({ box, img, crop, maxSide, gridRev }) {
  const ref = useRef(null);
  const cropRef = useRef(crop);
  cropRef.current = crop;

  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    const painted = output(box, img, cropRef.current, maxSide);
    node.width = painted.width;
    node.height = painted.height;
    const ctx = node.getContext('2d');
    ctx.drawImage(painted, 0, 0);
    return undefined;
  }, [box, img, maxSide, gridRev]);

  return <canvas ref={ref} />;
}

export default function CardCropperPage() {
  const dispatch = useDispatch();
  const displayedGradient = useSelector(selectDisplayedGradient);
  const teamIndex = useSelector(selectTeamIndex);
  const gradientData = useSelector(selectGradientData);
  const [dropOver, setDropOver] = useState(false);
  const closeRef = useRef(null);
  const wasOpen = useRef(false);
  const cropper = useCardCropper();
  const {
    scans,
    settings,
    crop,
    selection,
    status,
    draft,
    setDraft,
    zipBusy,
    gridRev,
    outputs,
    viewerIndex,
    viewerItem,
    addFiles,
    clearAll,
    detectMissing,
    clearBoxes,
    removeScan,
    toggleSkip,
    setTilt,
    rotatePhoto,
    createBox,
    selectBox,
    removeBox,
    onLive,
    onCommit,
    setSens,
    setPad,
    setPrefix,
    setFmt,
    setEbay,
    setBg,
    openViewer,
    closeViewer,
    stepViewer,
    saveOutput,
    downloadAll,
  } = cropper;

  useEffect(() => {
    if (!displayedGradient) dispatch(setDisplayedGradient('A'));
  }, [dispatch, displayedGradient]);

  useEffect(() => {
    if (viewerItem && !wasOpen.current) closeRef.current?.focus();
    wasOpen.current = Boolean(viewerItem);
  }, [viewerItem]);

  const numbers = useMemo(() => new Map(outputs.map((item) => [item.box, item.n])), [outputs]);
  const degrees = selection ? (R(selection.box).angle * 180) / Math.PI : 0;

  const onDrop = (e) => {
    e.preventDefault();
    setDropOver(false);
    addFiles(e.dataTransfer.files);
  };

  return (
    <Page>
      <GradientWrap>
        <GradientBG setGradient={gradientData[teamIndex] ?? gradientData[0]} />
      </GradientWrap>
      <HeaderWrap>
        <PageNav />
      </HeaderWrap>
      <Main $editing={Boolean(selection)}>
        <h1 className="pageHeader">Card Cropper</h1>
        <Lead>Find each card on a scan, straighten it, and download a photo for the listing.</Lead>

        <Drop
          className={dropOver ? 'over' : undefined}
          onDragEnter={(e) => { e.preventDefault(); setDropOver(true); }}
          onDragOver={(e) => { e.preventDefault(); setDropOver(true); }}
          onDragLeave={(e) => { e.preventDefault(); setDropOver(false); }}
          onDrop={onDrop}
        >
          <svg viewBox="0 0 64 48" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 38H15a11 11 0 0 1-1.6-21.9A15 15 0 0 1 42.3 11 11.5 11.5 0 0 1 50 38h-6" />
            <path d="M32 44V22M24 29l8-8 8 8" />
          </svg>
          <DropTitle>Drag and drop scans here</DropTitle>
          <DropOr>or</DropOr>
          <Btn as="span">Browse files</Btn>
          <input
            type="file"
            accept="image/*"
            multiple
            hidden
            onChange={(e) => {
              addFiles(e.target.files);
              e.target.value = '';
            }}
          />
        </Drop>

        {scans.length > 0 && (
          <>
            <Toolbar>
              <Field>
                <FieldLabel>Sensitivity</FieldLabel>
                <RangeRow>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={settings.sens}
                    aria-label="Sensitivity"
                    onChange={(e) => setSens(e.target.value)}
                  />
                  <Value>{settings.sens}</Value>
                </RangeRow>
              </Field>
              <Field>
                <FieldLabel>Edge margin</FieldLabel>
                <RangeRow>
                  <input
                    type="range"
                    min="-3"
                    max="8"
                    step="0.5"
                    value={settings.pad}
                    aria-label="Edge margin"
                    onChange={(e) => setPad(e.target.value)}
                  />
                  <Value>{Number(settings.pad).toFixed(1)}%</Value>
                </RangeRow>
              </Field>
              <Field>
                <FieldLabel>File name prefix</FieldLabel>
                <TextInput
                  value={settings.prefix}
                  spellCheck={false}
                  aria-label="File name prefix"
                  onChange={(e) => setPrefix(e.target.value)}
                />
              </Field>
              <Field>
                <FieldLabel>Format</FieldLabel>
                <FormatSelect
                  value={settings.fmt}
                  aria-label="Format"
                  onChange={(e) => setFmt(e.target.value)}
                >
                  <option value="jpg">JPEG (95%)</option>
                  <option value="png">PNG</option>
                </FormatSelect>
              </Field>
            </Toolbar>

            <Bench>
              <Pane>
                <PaneHead>
                  <h2>Scans</h2>
                </PaneHead>
                <Hint>
                  Click a box to select it, then move, resize, rotate, skip or delete it. Drag on empty space to draw a box for a card that was missed.
                </Hint>
                <Scans>
                  {scans.map((scan, index) => (
                    <ScanBlock key={scan.id}>
                      <ScanMeta>
                        <b>{`${index + 1} of ${scans.length} • ${scan.name}`}</b>
                        <PaneActions>
                          <Btn
                            type="button"
                            $size="sm"
                            title="Add boxes for detected cards that don't have one yet"
                            onClick={() => detectMissing(scan)}
                          >
                            Detect cards
                          </Btn>
                          <Btn type="button" $size="sm" onClick={() => clearBoxes(scan)}>Remove all boxes</Btn>
                          <Btn type="button" $size="sm" onClick={() => removeScan(scan)}>Remove scan</Btn>
                        </PaneActions>
                      </ScanMeta>
                      <ScanStage
                        scan={scan}
                        selectedBox={selection?.scan === scan ? selection.box : null}
                        pad={crop.pad}
                        numbers={numbers}
                        draft={draft}
                        onSelect={selectBox}
                        onLive={onLive}
                        onCommit={onCommit}
                        onCreate={createBox}
                        onDraft={setDraft}
                      />
                    </ScanBlock>
                  ))}
                </Scans>
              </Pane>

              <Pane>
                <PaneHead>
                  <h2>
                    Listing photos
                    {outputs.length ? ` (${outputs.length})` : ''}
                  </h2>
                  <PaneActions>
                    <Check as="span">
                      <input
                        id="card-cropper-bg"
                        type="checkbox"
                        checked={settings.ebay}
                        onChange={(e) => setEbay(e.target.checked)}
                      />
                      <label htmlFor="card-cropper-bg" title="Export as a 1600×1600 square with the card filling 90%">
                        Add background
                      </label>
                      <input
                        type="color"
                        value={settings.bg}
                        aria-label="Background color"
                        title="Background color"
                        disabled={!settings.ebay}
                        onChange={(e) => setBg(e.target.value)}
                      />
                    </Check>
                    <Btn type="button" onClick={clearAll}>Clear all</Btn>
                    <Btn type="button" onClick={downloadAll} disabled={!outputs.length || zipBusy}>
                      {zipBusy ? 'Preparing…' : 'Download all (.zip)'}
                    </Btn>
                  </PaneActions>
                </PaneHead>
                {status.msg && <Status $kind={status.cls} role="status">{status.msg}</Status>}
                {outputs.length === 0 ? (
                  <Empty>No cards found yet. Try raising sensitivity, or drag a box on the scan.</Empty>
                ) : (
                  <PhotoGrid>
                    {outputs.map((item) => {
                      const tag = boxTag(item.box);
                      const name = fileName(item.n, crop);
                      return (
                        <Photo key={`${item.scan.id}-${item.box.id}`}>
                          <PhotoFrame $square={crop.ebay}>
                            <CropCanvas
                              box={item.box}
                              img={item.scan.img}
                              crop={crop}
                              maxSide={420}
                              gridRev={gridRev}
                            />
                            <ViewButton type="button" aria-label={`View ${name}`} onClick={() => openViewer(item.n)}>
                              <svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12z" />
                                <circle cx="12" cy="12" r="3" />
                              </svg>
                              <span>VIEW</span>
                            </ViewButton>
                          </PhotoFrame>
                          <PhotoRow>
                            <PhotoName>{name}</PhotoName>
                            {tag && <Tag $warn={tag.warn}>{tag.label}</Tag>}
                          </PhotoRow>
                          <PhotoRow>
                            <ToolRow>
                              <Btn type="button" $icon title="Rotate left" aria-label="Rotate left" onClick={() => rotatePhoto(item.box, 3)}>
                                <IconRotate size={16} stroke={2} color="#FFFFFF" />
                              </Btn>
                              <Btn type="button" $icon title="Rotate right" aria-label="Rotate right" onClick={() => rotatePhoto(item.box, 1)}>
                                <IconRotateClockwise size={16} stroke={2} color="#FFFFFF" />
                              </Btn>
                            </ToolRow>
                            <Btn type="button" $icon title="Download this photo" aria-label="Download this photo" onClick={() => saveOutput(item)}>
                              <IconDownload size={16} stroke={2} color="#FFFFFF" />
                            </Btn>
                          </PhotoRow>
                        </Photo>
                      );
                    })}
                  </PhotoGrid>
                )}
              </Pane>
            </Bench>
          </>
        )}
      </Main>

      {selection && (
        <EditDock role="region" aria-label="Edit selected box">
          <EditRow>
            <FieldLabel>{numbers.get(selection.box) ? `Box ${numbers.get(selection.box)}` : 'Skipped box'}</FieldLabel>
            <Tilt>
              <label htmlFor={`tilt-${selection.scan.id}`}>Tilt</label>
              <Btn type="button" $size="sm" aria-label="Tilt left 0.5 degrees" onClick={() => setTilt(selection.box, degrees - 0.5)}>−</Btn>
              <input
                id={`tilt-${selection.scan.id}`}
                type="range"
                min="-45"
                max="45"
                step="0.1"
                value={Math.max(-45, Math.min(45, degrees))}
                onChange={(e) => setTilt(selection.box, Number(e.target.value))}
              />
              <Btn type="button" $size="sm" aria-label="Tilt right 0.5 degrees" onClick={() => setTilt(selection.box, degrees + 0.5)}>+</Btn>
              <Value>{degrees.toFixed(1)}°</Value>
              <Btn type="button" $size="sm" title="Set tilt to 0°" onClick={() => setTilt(selection.box, 0)}>Reset</Btn>
            </Tilt>
            <EditActions>
              <Btn type="button" $size="sm" onClick={() => toggleSkip(selection.box)}>
                {selection.box.on ? 'Skip' : 'Include'}
              </Btn>
              <Btn type="button" $size="sm" $variant="danger" onClick={() => removeBox(selection.scan, selection.box)}>
                Delete box
              </Btn>
              <Btn type="button" $size="sm" $variant="go" onClick={() => selectBox(null, null)}>Done</Btn>
            </EditActions>
          </EditRow>
          <Hint>
            Drag the box to move it, a corner to resize, or the round handle to rotate. Keys: Delete removes, [ and ] tilt, arrows nudge.
          </Hint>
        </EditDock>
      )}

      {viewerItem && (
        <Viewer
          role="dialog"
          aria-modal="true"
          aria-labelledby="cropper-viewer-name"
          onClick={closeViewer}
        >
          <ViewerBox onClick={(e) => e.stopPropagation()}>
            <ViewerHead>
              <ViewerTitle>
                <span id="cropper-viewer-name">{fileName(viewerItem.n, crop)}</span>
                <Dims>
                  {outputSize(viewerItem.box, crop).join('×')}
                  {` · ${viewerIndex} of ${outputs.length}`}
                </Dims>
              </ViewerTitle>
              <PaneActions>
                <Btn type="button" $size="sm" onClick={() => saveOutput(viewerItem)}>Save</Btn>
                <Btn type="button" $size="sm" ref={closeRef} aria-label="Close preview" onClick={closeViewer}>✕</Btn>
              </PaneActions>
            </ViewerHead>
            <ViewerBody>
              <NavBtn type="button" aria-label="Previous photo" disabled={viewerIndex <= 1} onClick={() => stepViewer(-1)}>‹</NavBtn>
              <ViewerStage onClick={(e) => { if (e.target === e.currentTarget) closeViewer(); }}>
                <CropCanvas
                  box={viewerItem.box}
                  img={viewerItem.scan.img}
                  crop={crop}
                  gridRev={gridRev}
                />
              </ViewerStage>
              <NavBtn type="button" aria-label="Next photo" disabled={viewerIndex >= outputs.length} onClick={() => stepViewer(1)}>›</NavBtn>
            </ViewerBody>
          </ViewerBox>
        </Viewer>
      )}
    </Page>
  );
}
